/**
 * LoginPage - the /login route for the SignalSentry client app.
 *
 * Responsibilities:
 *   1. Redirect already-authenticated users to their actor-appropriate landing
 *      (staff with no impersonation -> /admin/fleet; everyone else -> returnTo
 *      or /now).
 *   2. Render LoginForm and wire submit to useAuth().login().
 *   3. After successful login: navigate to actor-appropriate landing.
 *
 * Phase 3.1 (ADR-006 section 1): the StaffEmptyState dead-end was retired.
 * Staff with impersonating_as=false now land on /admin/fleet (the route is
 * registered for them by buildRoutes in src/router/build-routes.tsx).
 *
 * Actor-type routing decision:
 *   The router itself does the heavy lifting via claim-aware registration;
 *   LoginPage just picks the destination URL after a successful login. The
 *   buildRoutes function ensures direct visits to mismatched URLs (e.g.
 *   client_user hitting /admin/fleet) fall to the SPA 404, so this
 *   navigation is a UX nicety, not a security gate.
 *
 * returnTo param:
 *   Set by requireAuthLoader when redirecting to /login. After a client
 *   login the user is sent back to their original destination (default
 *   /now). Staff users always go to /admin/fleet regardless of returnTo
 *   because their route surface does not include the requested URL.
 *
 * Modified: 2026-04-25
 */
import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/features/auth/useAuth';
import { LoginForm } from '@/features/auth/LoginForm';
import type { User } from '@/features/auth/schemas';

/**
 * Resolve the post-login landing URL for a given authenticated user.
 *
 * Internal staff with impersonating_as=false always go to /admin/fleet
 * because the client routes are not registered in their session. Everyone
 * else (client_user, or staff currently impersonating an org) uses the
 * caller-provided returnTo (defaults to /now).
 *
 * @param user - The authenticated User object.
 * @param returnTo - The caller-supplied returnTo path (default '/now').
 * @returns The path to navigate to after login.
 */
function landingForUser(user: User, returnTo: string): string {
  if (user.actor_type === 'internal_staff' && !user.impersonating_as) {
    return '/admin/fleet';
  }
  return returnTo;
}

/**
 * LoginPage component.
 *
 * Manages the full login flow including post-login navigation and
 * already-authenticated short-circuit handling.
 */
export function LoginPage() {
  const { user, status, login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo') ?? '/now';

  /**
   * If the user is already authenticated (e.g. browser back after login,
   * or a session that survived a reload), skip the login page entirely
   * and route to their actor-appropriate landing.
   */
  useEffect(() => {
    if (status === 'authenticated' && user !== null) {
      navigate(landingForUser(user, returnTo), { replace: true });
    }
  }, [status, user, navigate, returnTo]);

  /**
   * Handles the LoginForm submit callback.
   *
   * Calls useAuth().login(), then navigates to the actor-appropriate
   * landing URL. AuthProvider has already updated user + status before
   * this resolves; the navigation triggers a router rebuild via
   * AppRouter's useMemo key.
   *
   * @param email - Email address from the form.
   * @param password - Password from the form.
   */
  const handleSubmit = async (email: string, password: string): Promise<void> => {
    const loggedInUser: User = await login(email, password);
    navigate(landingForUser(loggedInUser, returnTo), { replace: true });
  };

  /**
   * While initializing (AuthProvider checking for an existing session on
   * mount), render nothing to avoid a flash of the login form before the
   * redirect fires.
   */
  if (status === 'initializing') {
    return null;
  }

  return <LoginForm onSubmit={handleSubmit} />;
}
